import { ApiBase, PageableResult } from '@bymason/api-base';
import {
  DeviceModel,
  GetDeviceOptions,
  GetDevicesOptions,
  NotifyDevicesOptions,
  DeviceStatus,
  RenameDeviceOptions,
  DeviceLocation,
  DevicePeripheralResponse,
  GetDevicePeripheralsOptions,
} from './types';

export * from './types';

class Devices extends ApiBase {
  private static instance: Devices;

  private constructor() {
    super();
  }

  /**
   * There is no need for consumers to ever call this method as its purely used to enforce a singleton module pattern.
   * @returns A Devices instance
   */
  public static getInstance(): Devices {
    if (!Devices.instance) {
      Devices.instance = new Devices();
    }

    return Devices.instance;
  }

  /**
   * Sends commands to devices
   * @param options metadata required to notify a device.
   * @param clientToken token required for proper authorization.
   * @returns either a list of device ids or void
   */
  public async notifyDevices(options: NotifyDevicesOptions, clientToken: string) {
    const { type, ...body } = options;
    if (options.type) {
      const urlTemplate = this.getEndpointTemplate('device/:type');
      const url = this.expandUrlParams(urlTemplate, { type });

      const dataResponse = await this.callVoidEndpoint({
        body: JSON.stringify(body),
        clientToken,
        method: 'POST',
        url,
      });

      return dataResponse;
    }
  }

  /**
   * Gets a device.
   * @param options metadata required to get a device.
   * @param clientToken token required for proper authorization.
   * @returns either a device model or void
   */
  public async getDevice(options: GetDeviceOptions, clientToken: string) {
    const urlTemplate = this.getEndpointTemplate('device/:deviceId');
    const url = this.expandUrlParams(urlTemplate, options);

    const dataResponse = await this.callJsonEndpoint<PageableResult<DeviceModel[]>>({
      clientToken,
      method: 'GET',
      useSessionCache: false,
      url,
    });

    return dataResponse && dataResponse.data && dataResponse.data.length ? dataResponse.data[0] : undefined;
  }

  /**
   * Gets status info for a device.
   * @param options metadata required to get a device.
   * @param clientToken token required for proper authorization.
   * @returns either a device status or void
   */
  public async getDeviceStatus(options: GetDeviceOptions, clientToken: string) {
    const urlTemplate = this.getEndpointTemplate('device/:deviceId/ping');
    const url = this.expandUrlParams(urlTemplate, options);

    const dataResponse = await this.callJsonEndpoint<DeviceStatus>({
      clientToken,
      method: 'GET',
      useSessionCache: false,
      url,
    });

    return dataResponse;
  }

  /**
   * Gets location info for a device.
   * @param options metadata required to get a device.
   * @param clientToken token required for proper authorization.
   * @returns either a device location or void
   */
  public async getDeviceLocation(options: GetDeviceOptions, clientToken: string) {
    const urlTemplate = this.getEndpointTemplate('device/:deviceId/locate');
    const url = this.expandUrlParams(urlTemplate, options);

    const dataResponse = await this.callJsonEndpoint<DeviceLocation>({
      clientToken,
      method: 'GET',
      useSessionCache: false,
      url,
    });

    return dataResponse;
  }

  /**
   * Renames a device.
   * @param options metadata required to rename a device.
   * @param clientToken token required for proper authorization.
   * @returns either a device model or void
   */
  public async renameDevice(options: RenameDeviceOptions, clientToken: string) {
    const { deviceId, ...body } = options;
    const urlTemplate = this.getEndpointTemplate('device/:deviceId');
    const url = this.expandUrlParams(urlTemplate, { deviceId });

    const dataResponse = await this.callJsonEndpoint<DeviceModel>({
      body: JSON.stringify(body),
      clientToken,
      method: 'POST',
      url,
    });

    return dataResponse;
  }

  /**
   * Gets paginated devices.
   * @param options metadata required to get devices.
   * @param clientToken token required for proper authorization.
   * @param signal abort conroller signal for fetch
   * @returns either void or a pageable structure that includes devices.
   */
  public async getDevices(options: GetDevicesOptions, clientToken: string, signal?: AbortSignal | null) {
    const urlTemplate = this.getEndpointTemplate('device');
    const url = this.expandUrlParams(urlTemplate, options, {
      addUnusedParamsToSearch: true,
      encodeParamValues: true,
      splitParamValuesOnComma: false,
    });

    const dataResponse = await this.callJsonEndpoint<PageableResult<DeviceModel[]>>({
      clientToken,
      errorOn404: false,
      method: 'GET',
      url,
      signal,
    });

    return dataResponse;
  }

  /**
   * Gets a list of perhepheral associated with the device.
   * @param options metadata required to get devices.
   * @param clientToken token required for proper authorization.
   * @param signal abort conroller signal for fetch
   * @returns either void or a pageable structure that includes devices.
   */
  public async getDevicePeripherals(
    options: GetDevicePeripheralsOptions,
    clientToken: string,
    signal?: AbortSignal | null
  ) {
    const urlTemplate = this.getEndpointTemplate('device/:deviceId/peripheral');
    const url = this.expandUrlParams(urlTemplate, options);

    const dataResponse = await this.callJsonEndpoint<DevicePeripheralResponse>({
      clientToken,
      errorOn404: false,
      method: 'GET',
      url,
      signal,
    });

    return dataResponse;
  }
}

export const devicesClient = Devices.getInstance();
